import { Check, ChevronDown, Settings, AlertCircle, Loader } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/logo";
import { useApp } from "@/context/app-context";
import { toDateStr, DAYS, DAY_LABELS, getCurrentCycleWeek, getActiveWeekSchedule } from "@/lib/helpers";

function getMuscleTags(exercises: Array<{ name: string }>): string[] {
  const categoryMap: Record<string, string> = {
    'bench press': 'Chest', 'incline press': 'Chest', 'chest fly': 'Chest', 'dumbbell press': 'Chest', 'push-up': 'Chest',
    'squat': 'Legs', 'leg press': 'Legs', 'leg extension': 'Legs', 'leg curl': 'Legs', 'lunge': 'Legs', 'hack squat': 'Legs',
    'deadlift': 'Back', 'barbell row': 'Back', 'pull-up': 'Back', 'lat pulldown': 'Back', 'cable row': 'Back', 'chin-up': 'Back',
    'overhead press': 'Shoulders', 'shoulder press': 'Shoulders', 'lateral raise': 'Shoulders', 'face pull': 'Shoulders',
    'bicep curl': 'Arms', 'tricep': 'Arms', 'hammer curl': 'Arms', 'skull crusher': 'Arms',
    'calf raise': 'Calves', 'romanian deadlift': 'Legs', 'hip thrust': 'Glutes',
  };
  const tags = new Set<string>();
  exercises.forEach(ex => {
    const name = ex.name.toLowerCase();
    for (const [key, group] of Object.entries(categoryMap)) {
      if (name.includes(key)) { tags.add(group); return; }
    }
    tags.add('General');
  });
  return Array.from(tags).slice(0, 3);
}

export function Schedule() {
  const navigate = useNavigate();
  const { state, dispatch, sheetsStatus } = useApp();

  const isWeekly = state.scheduleType === "weekly";
  const isMultiWeek = state.scheduleType === "multiweek";
  const isCadence = state.scheduleType === "cadence";

  const mw = state.multiWeekSchedule;
  const autoWeekIdx = isMultiWeek
    ? getCurrentCycleWeek(mw.startDate, mw.cycleLength)
    : 0;

  const [selectedWeek, setSelectedWeek] = useState(autoWeekIdx);

  const today = new Date();
  const dayIdx = today.getDay();
  const todayDayName = DAYS[dayIdx === 0 ? 6 : dayIdx - 1];

  // Get the schedule for the selected/viewed week
  const getWeekSchedule = (weekIdx: number) => {
    if (isMultiWeek && mw.weeks[weekIdx]) {
      return mw.weeks[weekIdx];
    }
    return state.weeklySchedule;
  };

  const activeSchedule = isMultiWeek ? getWeekSchedule(selectedWeek) : state.weeklySchedule;

  // Get workout assignments for the grid
  const workoutAssignments = DAYS.map((day) => {
    const slot = activeSchedule.find((s) => s.day === day);
    const workout = slot && slot.workoutId ? state.workouts[slot.workoutId] : null;
    return workout?.name || "Rest";
  });

  // Get upcoming workouts from the currently active week (auto-detected)
  const currentActiveSchedule = getActiveWeekSchedule(state);
  const upcomingWorkouts = DAYS.map((day) => {
    const slot = currentActiveSchedule.find((s) => s.day === day);
    const workout = slot && slot.workoutId ? state.workouts[slot.workoutId] : null;
    const isRest = !workout;
    const estimatedMinutes = workout ? Math.max(30, 10 + workout.exercises.length * 5) : 0;

    return {
      day: DAY_LABELS[day],
      dayFull: day,
      name: workout?.name || "Rest Day",
      exercises: workout?.exercises.length || null,
      sets: workout ? `${workout.exercises.length} exercises` : "",
      muscleGroups: workout ? getMuscleTags(workout.exercises) : ["Recovery"],
      time: isRest ? "" : `~${estimatedMinutes} min`,
      isRest,
      workout,
    };
  });

  // Check which days have tracked workouts this week
  const weekStart = new Date(today);
  weekStart.setDate(weekStart.getDate() - dayIdx + 1);
  const weekStartStr = toDateStr(weekStart);
  const completedDates = new Set(
    state.history
      .filter((h) => h.date >= weekStartStr && h.date <= toDateStr(new Date(weekStart.getTime() + 6 * 86400000)))
      .map((h) => h.date)
  );

  const dayStatuses = DAYS.map((_, idx) => {
    const checkDate = new Date(weekStart);
    checkDate.setDate(checkDate.getDate() + idx);
    return completedDates.has(toDateStr(checkDate));
  });

  // Rotation workouts (cadence mode)
  const rotationWorkouts = state.cadenceSchedule.rotation?.slice(0, 3).map((item) => {
    const workout = state.workouts[item.workoutId];
    return {
      name: workout?.name || "Unknown",
      exercises: workout?.exercises.map((e) => e.name).join(", ") || "",
      sets: workout?.exercises.length || 0,
    };
  }) || [];

  // Sync badge logic
  const getSyncBadgeStyle = () => {
    if (!state.gapiToken) {
      return { bgColor: "#3F3F46", textColor: "#A1A1AA", icon: null, label: "Offline" };
    }
    if (sheetsStatus === 'syncing') {
      return { bgColor: "#78350F", textColor: "#FCD34D", icon: Loader, label: "Syncing..." };
    }
    if (sheetsStatus === 'error') {
      return { bgColor: "#7F1D1D", textColor: "#FCA5A5", icon: AlertCircle, label: "Sync Error" };
    }
    if (sheetsStatus === 'paused') {
      return { bgColor: "#3F3F46", textColor: "#A1A1AA", icon: null, label: "Offline" };
    }
    return { bgColor: "#1F4428", textColor: "#4ADE80", icon: Check, label: "Synced" };
  };

  const badgeStyle = getSyncBadgeStyle();
  const BadgeIcon = badgeStyle.icon;

  return (
    <div className="p-5 pb-24" style={{ backgroundColor: "#18181B" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5" style={{ height: "32px" }}>
        <Logo />
        <div
          className="px-2.5 py-1 rounded-full flex items-center gap-1"
          style={{ backgroundColor: badgeStyle.bgColor, color: badgeStyle.textColor, fontSize: "11px", fontWeight: 600 }}
        >
          {BadgeIcon && <BadgeIcon size={10} />}
          <span>{badgeStyle.label}</span>
        </div>
      </div>

      {/* Title and Toggle */}
      <div className="flex items-center justify-between mb-5">
        <h2 style={{ color: "#F0F0F0", fontSize: "18px", fontWeight: 600 }}>
          Schedule
        </h2>
        <div
          className="flex items-center rounded-full p-1"
          style={{ backgroundColor: "#27272A" }}
        >
          <button
            className="px-3 py-1.5 rounded-full transition-colors"
            style={{
              backgroundColor: isWeekly ? "#FF6B35" : "transparent",
              color: isWeekly ? "#18181B" : "#71717A",
              fontSize: "11px",
              fontWeight: 600,
            }}
            onClick={() => dispatch({ type: "SET_SCHEDULE_TYPE", scheduleType: "weekly" })}
          >
            Weekly
          </button>
          <button
            className="px-3 py-1.5 rounded-full transition-colors"
            style={{
              backgroundColor: isMultiWeek ? "#FF6B35" : "transparent",
              color: isMultiWeek ? "#18181B" : "#71717A",
              fontSize: "11px",
              fontWeight: 600,
            }}
            onClick={() => dispatch({ type: "SET_SCHEDULE_TYPE", scheduleType: "multiweek" })}
          >
            Cycle
          </button>
          <button
            className="px-3 py-1.5 rounded-full transition-colors"
            style={{
              backgroundColor: isCadence ? "#FF6B35" : "transparent",
              color: isCadence ? "#18181B" : "#71717A",
              fontSize: "11px",
              fontWeight: 600,
            }}
            onClick={() => dispatch({ type: "SET_SCHEDULE_TYPE", scheduleType: "cadence" })}
          >
            Rotating
          </button>
        </div>
      </div>

      <p style={{ color: "#71717A", fontSize: "13px", marginBottom: "20px", lineHeight: "1.6" }}>
        {isWeekly && "Plan your workouts for the week with fixed days and recurring exercises."}
        {isMultiWeek && `${mw.cycleLength}-week rotating cycle. Each week has its own workout plan.`}
        {isCadence && "Cycles through workouts regardless of day for flexible training."}
      </p>

      {(isWeekly || isMultiWeek) && (
        <>
          {/* Multi-Week Cycle Tabs */}
          {isMultiWeek && (
            <div
              className="p-3 mb-5 rounded-xl"
              style={{ backgroundColor: "#27272A" }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {Array.from({ length: mw.cycleLength }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedWeek(i)}
                      className="px-3 py-1.5 rounded-full"
                      style={{
                        backgroundColor: selectedWeek === i ? "#FF6B35" : "#3F3F46",
                        color: selectedWeek === i ? "#18181B" : "#71717A",
                        fontSize: "12px",
                        fontWeight: 700,
                        minWidth: "64px",
                        border: autoWeekIdx === i && selectedWeek !== i ? "1px solid #FF6B35" : "none",
                      }}
                    >
                      Week {i + 1}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => navigate("/schedule-config")}
                  className="rounded-lg flex items-center justify-center"
                  style={{
                    minWidth: "32px",
                    minHeight: "32px",
                    backgroundColor: "#3F3F46",
                    color: "#71717A",
                  }}
                >
                  <Settings size={16} />
                </button>
              </div>

              {/* Current week indicator */}
              <div className="flex items-center justify-between mb-2">
                <span style={{ color: "#71717A", fontSize: "11px", fontWeight: 600 }}>
                  {selectedWeek === autoWeekIdx ? "CURRENT WEEK" : "VIEWING"}
                </span>
                <span style={{ color: "#71717A", fontSize: "11px" }}>
                  Week {autoWeekIdx + 1} of {mw.cycleLength} active
                </span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "#3F3F46" }}>
                <div
                  className="h-full"
                  style={{
                    width: `${((autoWeekIdx + 1) / mw.cycleLength) * 100}%`,
                    background: "linear-gradient(90deg, #FFA033 0%, #FF6B35 50%, #E8432A 100%)",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>
          )}

          {/* Weekly mode: just a settings button */}
          {isWeekly && (
            <div className="flex justify-end mb-4">
              <button
                onClick={() => navigate("/schedule-config")}
                className="rounded-lg flex items-center justify-center gap-2 px-3 py-2"
                style={{
                  backgroundColor: "#27272A",
                  color: "#A1A1AA",
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                <Settings size={14} />
                Edit Schedule
              </button>
            </div>
          )}

          {/* Weekly Grid */}
          <div className="grid grid-cols-7 gap-2 mb-6">
            {DAYS.map((day, index) => (
              <div key={day} className="flex flex-col gap-2">
                <div
                  className="flex items-center justify-center rounded-lg"
                  style={{
                    backgroundColor: "#27272A",
                    height: "44px",
                    color: "#F0F0F0",
                    fontSize: "11px",
                    fontWeight: 600,
                  }}
                >
                  {DAY_LABELS[day]}
                </div>
                <div
                  className="flex items-center justify-center rounded-lg text-center px-1"
                  style={{
                    backgroundColor: workoutAssignments[index] === "Rest" ? "#3F3F46" : "#FF6B35",
                    minHeight: "60px",
                    color: workoutAssignments[index] === "Rest" ? "#71717A" : "#18181B",
                    fontSize: "11px",
                    fontWeight: 700,
                  }}
                >
                  {workoutAssignments[index]}
                </div>
                {dayStatuses[index] && (
                  <div className="flex items-center justify-center">
                    <Check size={16} style={{ color: "#4ADE80" }} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* 7-Day Strip */}
          <div className="grid grid-cols-7 gap-1.5 mb-5">
            {Array.from({ length: 7 }).map((_, index) => (
              <div
                key={index}
                className="rounded"
                style={{
                  height: "6px",
                  backgroundColor: dayStatuses[index] ? "#4ADE80" : "#3F3F46",
                }}
              />
            ))}
          </div>
        </>
      )}

      {isCadence && (
        <>
          {/* Cadence Mode Display */}
          <div
            className="p-3 mb-5 rounded-xl"
            style={{ backgroundColor: "#27272A" }}
          >
            <div className="flex items-center justify-between mb-3">
              <span style={{ color: "#F0F0F0", fontSize: "14px", fontWeight: 600 }}>
                Rotation Schedule
              </span>
              <button
                onClick={() => navigate("/schedule-config")}
                className="rounded-lg flex items-center justify-center"
                style={{
                  minWidth: "32px",
                  minHeight: "32px",
                  backgroundColor: "#3F3F46",
                  color: "#71717A",
                }}
              >
                <Settings size={16} />
              </button>
            </div>
            <p style={{ color: "#71717A", fontSize: "12px", marginBottom: "12px" }}>
              {state.cadenceSchedule.rotation?.length || 0} workouts rotating
              {state.cadenceSchedule.cycleLengthDays && ` every ${state.cadenceSchedule.cycleLengthDays} days`}
            </p>
          </div>

          {/* Cadence Rotation Display */}
          {state.cadenceSchedule.rotation && state.cadenceSchedule.rotation.length > 0 && (
            <div className="space-y-3 mb-6">
              {state.cadenceSchedule.rotation.slice(0, 3).map((item, index) => {
                const workout = state.workouts[item.workoutId];
                return (
                  <div
                    key={index}
                    className="p-4"
                    style={{
                      backgroundColor: "#27272A",
                      borderRadius: "16px",
                      border: "2px solid #FF6B35",
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span style={{ color: "#F0F0F0", fontSize: "15px", fontWeight: 700 }}>
                        {workout?.name || "Unknown"}
                      </span>
                    </div>
                    <div style={{ color: "#71717A", fontSize: "13px" }}>
                      {workout?.exercises.map((e) => e.name).join(", ")}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Upcoming Section */}
      <div className="flex items-center justify-between mb-4">
        <span style={{ color: "#F0F0F0", fontSize: "13px", fontWeight: 600, letterSpacing: "0.5px" }}>
          UPCOMING
        </span>
      </div>

      <div className="space-y-3 mb-5">
        {upcomingWorkouts.map((workout, index) => (
          <div
            key={index}
            className="p-4"
            style={{
              backgroundColor: "#27272A",
              borderRadius: "16px",
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ color: "#71717A", fontSize: "12px", fontWeight: 600 }}>
                    {workout.day}
                  </span>
                  <span style={{ color: "#F0F0F0", fontSize: "15px", fontWeight: 700 }}>
                    {workout.name}
                  </span>
                </div>
                {workout.muscleGroups.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {workout.muscleGroups.map((muscle, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 rounded-full"
                        style={{
                          backgroundColor: muscle === "Recovery" ? "#3F3F46" : "#FF6B35",
                          color: muscle === "Recovery" ? "#71717A" : "#18181B",
                          fontSize: "11px",
                          fontWeight: 600,
                        }}
                      >
                        {muscle}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <ChevronDown size={18} style={{ color: "#71717A" }} />
            </div>
            {workout.exercises && (
              <div className="flex items-center gap-4" style={{ color: "#71717A", fontSize: "12px" }}>
                <span>{workout.exercises} exercises</span>
                {workout.time && <span>{workout.time}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
